import axios from "axios";
import { emitArchiveInvalidate } from "./archive-events";
import { attachSessionExpiryInterceptor } from "./sessionExpiry";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;
export const API_TIMEOUT_MS = 90000;

const client = attachSessionExpiryInterceptor(
  axios.create({ baseURL: API, timeout: API_TIMEOUT_MS }));
export { client };

// =====================================================================
// Site-wide data sync — every admin mutation triggers a global cache
// invalidation so any open public page (Archive, Member Desk, Operative
// Card, Audience, etc.) refetches its data on the next render.
// =====================================================================

// Per-path-prefix scope mapping. First match wins. `*` is the catch-all.
const INVALIDATION_MAP = [
  [/^\/admin\/members\b/,           ["members"]],
  [/^\/admin\/email\/hub\/templates/, ["emails"]],
  [/^\/admin\/email\/hub\/drafts/,  ["emails"]],
  [/^\/admin\/email\/hub\/send/,    ["emails", "members"]],
  [/^\/admin\/email\/.*\/retry/,    ["emails"]],
  [/^\/admin\/newsletter/,          ["emails", "newsletters"]],
  [/^\/admin\/dossiers\//,          ["topics", "submissions"]],
  [/^\/admin\/topics\b/,            ["topics"]],
  [/^\/admin\/submissions\b/,       ["submissions"]],
  [/^\/admin\/drafts\b/,            ["submissions", "topics"]],
  [/^\/admin\/sources\b/,           ["sources", "topics"]],
  [/^\/admin\/comments\b/,          ["comments"]],
  [/^\/admin\/automation\b/,        ["automation"]],
  [/^\/admin\/permissions/,         ["permissions"]],
  [/^\/admin\/rbac\//,              ["permissions"]],
  [/^\/admin\/users\b/,             ["users"]],
  [/^\/admin\/site\/notice/,        ["site"]],
  [/^\/admin\/epstein/,             ["epstein"]],
];

const _invalidateForUrl = (url) => {
  if (!url) return;
  // axios urls may be absolute — strip baseURL
  const path = url.startsWith("http") ? new URL(url).pathname.replace(/^\/api/, "") : url;
  for (const [pattern, scopes] of INVALIDATION_MAP) {
    if (pattern.test(path)) {
      emitArchiveInvalidate(scopes);
      return;
    }
  }
  // Unknown admin path → broadcast generic invalidation so listeners that
  // subscribe to "*" still refresh.
  if (path && path.startsWith("/admin/")) emitArchiveInvalidate("*");
};

// Attach a response interceptor that fires emitArchiveInvalidate AFTER any
// mutating admin call (POST/PUT/PATCH/DELETE). Reads are not invalidated.
const _attachInvalidationInterceptor = (instance) => {
  instance.interceptors.response.use(
    (response) => {
      try {
        const m = (response?.config?.method || "").toUpperCase();
        if (m === "POST" || m === "PUT" || m === "PATCH" || m === "DELETE") {
          _invalidateForUrl(response?.config?.url || "");
        }
      } catch { /* noop */ }
      return response;
    },
    (err) => Promise.reject(err),
  );
};

export const fetchTopics = (params = {}) => client.get("/topics", { params }).then((r) => r.data);
export const fetchTrending = (limit = 8) => client.get("/topics/trending", { params: { limit } }).then((r) => r.data);

export const topicsSuggest = (q, limit = 8) =>
  client.get("/topics/suggest", { params: { q, limit } }).then((r) => r.data);

/** Grouped global search across dossiers, people, organizations, operations. */
export const globalSearch = (q, limitPerGroup = 5) =>
  client.get("/search/global", { params: { q, limit_per_group: limitPerGroup } }).then((r) => r.data);
export const fetchTopic = (id) => client.get(`/topics/${id}`).then((r) => r.data);
export const fetchCategories = () => client.get("/categories").then((r) => r.data);
export const fetchStats = () => client.get("/stats").then((r) => r.data);
export const generateTopic = (title, category) =>
  client.post("/topics/generate", { title, category }).then((r) => r.data);
export const fetchComments = (id) => client.get(`/topics/${id}/comments`).then((r) => r.data);
export const postComment = (data) => client.post("/comments", data).then((r) => r.data);
export const submitTopic = (data) => client.post("/submissions", data).then((r) => r.data);

// ---- Admin: accepts either operator token (header) OR OAuth Bearer ----
const adminClient = (token, userToken = null) => {
  const inst = axios.create({
  baseURL: API,
  timeout: 180000,
  headers: {
    ...(userToken ? { Authorization: `Bearer ${userToken}` } : {}),
  },
  // The legacy x-admin-token can travel as a header too. Both the FastAPI
  // Header(...) (auth/me) and Query(...) (admin submissions/comments) routes
  // read it; sending both is harmless.
  ...(token ? {
    headers: {
      ...(userToken ? { Authorization: `Bearer ${userToken}` } : {}),
      "x-admin-token": token,
    },
    params: { "x-admin-token": token },
  } : {}),
  });
  _attachInvalidationInterceptor(inst);
  return inst;
};

export const adminMe = (token = null, userToken = null) =>
  adminClient(token, userToken).get("/admin/me").then((r) => r.data);
export const adminOtpSend = (userToken) =>
  adminClient(null, userToken).post("/admin/otp/send").then((r) => r.data);
export const adminOtpVerify = (userToken, code) =>
  adminClient(null, userToken).post("/admin/otp/verify", { code }).then((r) => r.data);
export const adminLock = (userToken) =>
  adminClient(null, userToken).post("/admin/lock").then((r) => r.data);
export const adminAuditLog = (token, userToken = null, limit = 100) =>
  adminClient(token, userToken).get("/admin/audit-log", { params: { limit, ...(token ? { "x-admin-token": token } : {}) } }).then((r) => r.data);

// ---- Oracle Knowledge Graph — auto-refresh health ----
export const oracleGraphStatus = (token = null, userToken = null) =>
  adminClient(token, userToken).get("/oracle/graph/status").then((r) => r.data);
export const oracleGraphRefresh = (token = null, userToken = null) =>
  adminClient(token, userToken).post("/oracle/graph/refresh").then((r) => r.data);

// ---- Admin Idle Lock (Deadman Switch) ----
export const adminIdleConfigGet = (token, userToken = null) =>
  adminClient(token, userToken).get("/admin/security/idle-config").then((r) => r.data);
export const adminIdleConfigSet = (token, userToken, timeoutMinutes) =>
  adminClient(token, userToken)
    .put("/admin/security/idle-config", { timeout_minutes: timeoutMinutes })
    .then((r) => r.data);
export const adminIdleTimeoutEvent = (token, userToken, payload) =>
  adminClient(token, userToken)
    .post("/admin/security/events/idle-timeout", payload)
    .then((r) => r.data);

// ---- Security Center (Owner-only) ----
export const securityCenterOverview = (token, userToken = null) =>
  adminClient(token, userToken).get("/admin/security-center/overview").then((r) => r.data);
export const securityCenterForceLogoutAll = (token, userToken, confirmPhrase) =>
  adminClient(token, userToken)
    .post("/admin/security-center/force-logout-all", { confirm_phrase: confirmPhrase })
    .then((r) => r.data);
export const securityCenterRotateSessions = (token, userToken, confirmPhrase) =>
  adminClient(token, userToken)
    .post("/admin/security-center/rotate-sessions", { confirm_phrase: confirmPhrase })
    .then((r) => r.data);
export const securityCenterExportCsvUrl = (token, days = 90) =>
  `${API}/admin/security-center/export.csv?days=${days}${token ? `&x-admin-token=${encodeURIComponent(token)}` : ""}`;

// ---- Oracle Operations Pulse ----
export const oracleSummary = (token, userToken = null) =>
  adminClient(token, userToken).get("/admin/oracle/summary").then((r) => r.data);
export const oracleEvents = (token, userToken = null, params = {}) =>
  adminClient(token, userToken).get("/admin/oracle/events", { params }).then((r) => r.data);
export const oracleMarkRead = (token, userToken = null, upToTs = null) =>
  adminClient(token, userToken)
    .post("/admin/oracle/mark-read", upToTs ? { up_to_ts: upToTs } : {})
    .then((r) => r.data);
export const oracleResolve = (token, userToken = null, ids = []) =>
  adminClient(token, userToken)
    .post("/admin/oracle/resolve", { ids })
    .then((r) => r.data);
export const oracleResolveBulk = (token, userToken = null, severities = [], category = null) =>
  adminClient(token, userToken)
    .post("/admin/oracle/resolve-bulk", { severities, ...(category ? { category } : {}) })
    .then((r) => r.data);
export const oracleHeartbeat = (token, userToken = null) =>
  adminClient(token, userToken).post("/admin/oracle/heartbeat", {}).then((r) => r.data);
export const oraclePresence = (token, userToken = null) =>
  adminClient(token, userToken).get("/admin/oracle/presence").then((r) => r.data);
export const oracleBriefing = (token, userToken = null) =>
  adminClient(token, userToken).get("/admin/oracle/briefing").then((r) => r.data);

// ---- TOTP (authenticator app) ----
export const adminTotpStatus = (userToken) =>
  adminClient(null, userToken).get("/admin/totp/status").then((r) => r.data);
export const adminTotpSetup = (token, userToken) =>
  adminClient(token, userToken).post("/admin/totp/setup").then((r) => r.data);
export const adminTotpEnable = (token, userToken, code) =>
  adminClient(token, userToken).post("/admin/totp/enable", { code }).then((r) => r.data);
export const adminTotpDisable = (token, userToken) =>
  adminClient(token, userToken).post("/admin/totp/disable").then((r) => r.data);
export const adminTotpVerify = (userToken, code) =>
  adminClient(null, userToken).post("/admin/totp/verify", { code }).then((r) => r.data);
export const adminTotpBootstrapSetup = (userToken) =>
  adminClient(null, userToken).post("/admin/totp/bootstrap-setup").then((r) => r.data);
export const adminTotpBootstrapEnable = (userToken, code) =>
  adminClient(null, userToken).post("/admin/totp/bootstrap-enable", { code }).then((r) => r.data);
export const adminTotpRegenBackupCodes = (token, userToken) =>
  adminClient(token, userToken).post("/admin/totp/regenerate-backup-codes").then((r) => r.data);

// ---- Emergency recovery codes ----
export const adminRecoveryGenerate = (token, userToken = null) =>
  adminClient(token, userToken).post("/admin/recovery/generate").then((r) => r.data);
export const adminRecoveryList = (token, userToken = null) =>
  adminClient(token, userToken).get("/admin/recovery/list").then((r) => r.data);
export const adminRecoveryRevoke = (token, codeId, userToken = null) =>
  adminClient(token, userToken).post(`/admin/recovery/revoke/${codeId}`).then((r) => r.data);
// Public recovery flow (no auth required)
export const adminRecoveryUse = (code) =>
  axios.post(`${API}/admin/recovery/use`, { code }).then((r) => r.data);
export const adminRecoveryVerify = (code) =>
  axios.post(`${API}/admin/recovery/verify`, { code }).then((r) => r.data);
export const adminListSubmissions = (token, status = "HUMAN_REVIEW", userToken = null) =>
  adminClient(token, userToken).get("/admin/submissions", { params: { status, ...(token ? { "x-admin-token": token } : {}) } }).then((r) => r.data);
export const adminDecide = (token, subId, action, reason, userToken = null) =>
  adminClient(token, userToken).post(`/admin/submissions/${subId}/decide`, { action, reason }, { params: token ? { "x-admin-token": token } : {} }).then((r) => r.data);

// ---- Editorial workflow (leads → investigation → drafts) ----
export const adminListLeads = (token, opts = {}, userToken = null) =>
  adminClient(token, userToken).get("/admin/leads", {
    params: { ...opts, ...(token ? { "x-admin-token": token } : {}) },
  }).then((r) => r.data);
export const adminApproveAsLead = (token, subId, reason, userToken = null) =>
  adminClient(token, userToken).post(`/admin/submissions/${subId}/approve-as-lead`,
    { reason }, { params: token ? { "x-admin-token": token } : {} }).then((r) => r.data);
export const adminInvestigate = (token, subId, opts = {}, userToken = null) =>
  adminClient(token, userToken).post(`/admin/submissions/${subId}/investigate`,
    opts, { params: token ? { "x-admin-token": token } : {} }).then((r) => r.data);
export const adminArchiveSubmission = (token, subId, reason, userToken = null) =>
  adminClient(token, userToken).post(`/admin/submissions/${subId}/archive`,
    { reason }, { params: token ? { "x-admin-token": token } : {} }).then((r) => r.data);
export const adminGetDraftBySubmission = (token, subId, userToken = null) =>
  adminClient(token, userToken).get(`/admin/submissions/${subId}/draft`,
    { params: token ? { "x-admin-token": token } : {} }).then((r) => r.data);
export const adminGetDraft = (token, draftId, userToken = null) =>
  adminClient(token, userToken).get(`/admin/drafts/${draftId}`,
    { params: token ? { "x-admin-token": token } : {} }).then((r) => r.data);
export const adminPatchDraft = (token, draftId, patch, userToken = null) =>
  adminClient(token, userToken).patch(`/admin/drafts/${draftId}`, patch,
    { params: token ? { "x-admin-token": token } : {} }).then((r) => r.data);
export const adminArchiveDraft = (token, draftId, userToken = null) =>
  adminClient(token, userToken).post(`/admin/drafts/${draftId}/archive`,
    {}, { params: token ? { "x-admin-token": token } : {} }).then((r) => r.data);
export const adminPublishDraft = (token, draftId, payload, userToken = null) =>
  adminClient(token, userToken).post(`/admin/drafts/${draftId}/publish`,
    { confirm: true, ...payload }, { params: token ? { "x-admin-token": token } : {} }).then((r) => r.data);
export const adminReviewFolder = (token, opts = {}, userToken = null) =>
  adminClient(token, userToken).get("/admin/review-folder", {
    params: { ...opts, ...(token ? { "x-admin-token": token } : {}) },
  }).then((r) => r.data);
export const adminDraftDecision = (token, draftId, decision, note, userToken = null) =>
  adminClient(token, userToken).post(`/admin/drafts/${draftId}/decision`,
    { decision, note }, { params: token ? { "x-admin-token": token } : {} }).then((r) => r.data);

// News Radar
export const adminRadarList = (token, status = null, userToken = null) =>
  adminClient(token, userToken).get("/admin/radar",
    { params: { ...(status ? { status } : {}), ...(token ? { "x-admin-token": token } : {}) } }).then((r) => r.data);
export const adminRadarScan = (token, categories = [], userToken = null) =>
  adminClient(token, userToken).post("/admin/radar/scan", { categories },
    { params: token ? { "x-admin-token": token } : {} }).then((r) => r.data);
export const adminRadarAction = (token, rid, action, note, userToken = null) =>
  adminClient(token, userToken).post(`/admin/radar/${rid}/action`, { action, note },
    { params: token ? { "x-admin-token": token } : {} }).then((r) => r.data);

// Draft AI actions
export const adminDraftFillGaps = (token, draftId, userToken = null) =>
  adminClient(token, userToken).post(`/admin/drafts/${draftId}/fill-gaps`, {},
    { params: token ? { "x-admin-token": token } : {} }).then((r) => r.data);
export const adminDraftPolish = (token, draftId, userToken = null) =>
  adminClient(token, userToken).post(`/admin/drafts/${draftId}/polish`, {},
    { params: token ? { "x-admin-token": token } : {} }).then((r) => r.data);
export const adminDraftLock = (token, draftId, locked, userToken = null) =>
  adminClient(token, userToken).post(`/admin/drafts/${draftId}/lock`, {},
    { params: { locked, ...(token ? { "x-admin-token": token } : {}) } }).then((r) => r.data);

// Automation
export const adminGetAutomation = (token, userToken = null) =>
  adminClient(token, userToken).get("/admin/automation",
    { params: token ? { "x-admin-token": token } : {} }).then((r) => r.data);
export const adminPatchAutomation = (token, patch, userToken = null) =>
  adminClient(token, userToken).patch("/admin/automation", patch,
    { params: token ? { "x-admin-token": token } : {} }).then((r) => r.data);
export const adminAnalytics = (token, days = 30, userToken = null) =>
  adminClient(token, userToken).get("/admin/analytics/summary", { params: { days, ...(token ? { "x-admin-token": token } : {}) } }).then((r) => r.data);

export const adminAudienceAnalytics = (token, days = 30, userToken = null) =>
  adminClient(token, userToken).get("/admin/analytics/audience", { params: { days, ...(token ? { "x-admin-token": token } : {}) } }).then((r) => r.data);

// ---- Live Audience snapshot (admin-only) ----
export const adminLiveAudienceSnapshot = (token, userToken = null) =>
  adminClient(token, userToken).get("/admin/audience/snapshot",
    { params: token ? { "x-admin-token": token } : {} }).then((r) => r.data);

// ---- Visitor dossier (admin-only) ----
export const adminAudienceVisitors = (token, params = {}, userToken = null) =>
  adminClient(token, userToken).get("/admin/audience/visitors", {
    params: { ...params, ...(token ? { "x-admin-token": token } : {}) },
  }).then((r) => r.data);

export const adminAudienceVisitorDossier = (token, sessionId, userToken = null) =>
  adminClient(token, userToken).get(
    `/admin/audience/visitors/${encodeURIComponent(sessionId)}`,
    { params: token ? { "x-admin-token": token } : {} },
  ).then((r) => r.data);

export const adminAudienceReach = (token, days = 7, userToken = null) =>
  adminClient(token, userToken).get("/admin/audience/reach", {
    params: { days, ...(token ? { "x-admin-token": token } : {}) },
  }).then((r) => r.data);

export const adminReachGlobe = (token, params = {}, userToken = null) =>
  adminClient(token, userToken).get("/admin/audience/reach/globe", {
    params: { ...params, ...(token ? { "x-admin-token": token } : {}) },
  }).then((r) => r.data);

export const adminReachLive = (token, userToken = null) =>
  adminClient(token, userToken).get("/admin/audience/reach/live", {
    params: token ? { "x-admin-token": token } : {},
  }).then((r) => r.data);

export const adminReachTimeline = (token, params = {}, userToken = null) =>
  adminClient(token, userToken).get("/admin/audience/reach/timeline", {
    params: { ...params, ...(token ? { "x-admin-token": token } : {}) },
  }).then((r) => r.data);

export const adminReachCountry = (token, cc, days = 7, userToken = null) =>
  adminClient(token, userToken).get(
    `/admin/audience/reach/country/${encodeURIComponent(cc)}`,
    { params: { days, ...(token ? { "x-admin-token": token } : {}) } },
  ).then((r) => r.data);

export const adminReachFeed = (token, limit = 20, userToken = null) =>
  adminClient(token, userToken).get("/admin/audience/reach/feed", {
    params: { limit, ...(token ? { "x-admin-token": token } : {}) },
  }).then((r) => r.data);

export const adminReachEvents = (token, limit = 40, userToken = null) =>
  adminClient(token, userToken).get("/admin/audience/reach/events", {
    params: { limit, ...(token ? { "x-admin-token": token } : {}) },
  }).then((r) => r.data);

// ---- Epstein Evidence Fragments ----
export const efFragmentsByEntity = (entitySlugOrId, opts = {}) =>
  client.get("/epstein/fragments", {
    params: { entity_slug: entitySlugOrId, ...opts },
  }).then((r) => r.data);

export const efFragmentsByRelationship = (relationshipId) =>
  client.get("/epstein/fragments", { params: { relationship_id: relationshipId } }).then((r) => r.data);

export const efFragmentsBySource = (sourceId) =>
  client.get("/epstein/fragments", { params: { source_id: sourceId } }).then((r) => r.data);

export const efFragmentsByTimeline = (timelineEventId) =>
  client.get("/epstein/fragments", { params: { timeline_event_id: timelineEventId } }).then((r) => r.data);

// ---- Epstein Flight Logs ----
export const efFlightLogsStats = () =>
  client.get("/epstein/flight-logs/stats").then((r) => r.data);

export const efFlightLogsList = (params = {}) =>
  client.get("/epstein/flight-logs", { params }).then((r) => r.data);

export const efFlightLogDetail = (eventId) =>
  client.get(`/epstein/flight-logs/${encodeURIComponent(eventId)}`).then((r) => r.data);

export const efAircraftList = () =>
  client.get("/epstein/aircraft").then((r) => r.data);

export const efAdminPublishFlightLogsStarter = (token, userToken = null) =>
  adminClient(token, userToken).post("/admin/epstein/publish-flight-logs-starter", {},
    { params: token ? { "x-admin-token": token } : {}, timeout: 120000 }).then((r) => r.data);


export const efAdminFragmentsList = (token, params = {}, userToken = null) =>
  adminClient(token, userToken).get("/admin/epstein/fragments", { params }).then((r) => r.data);

export const efAdminFragmentCreate = (token, payload, userToken = null) =>
  adminClient(token, userToken).post("/admin/epstein/fragments", payload).then((r) => r.data);

export const efAdminFragmentPatch = (token, fragmentId, payload, userToken = null) =>
  adminClient(token, userToken).patch(`/admin/epstein/fragments/${fragmentId}`, payload).then((r) => r.data);

export const efAdminFragmentDelete = (token, fragmentId, userToken = null) =>
  adminClient(token, userToken).delete(`/admin/epstein/fragments/${fragmentId}`).then((r) => r.data);

export const efAdminFragmentAttach = (token, fragmentId, target, userToken = null) =>
  adminClient(token, userToken).post(`/admin/epstein/fragments/${fragmentId}/attach`, target).then((r) => r.data);

export const efAdminFragmentDetach = (token, fragmentId, target, userToken = null) =>
  adminClient(token, userToken).post(`/admin/epstein/fragments/${fragmentId}/detach`, target).then((r) => r.data);

// ---- Epstein Editorial Lifecycle (rollback / unpublish / quarantine / archive) ----
export const efAdminLifecycleImpact = (token, objectType, objectId, userToken = null, opts = {}) =>
  adminClient(token, userToken).get("/admin/epstein/lifecycle/impact",
    { params: { object_type: objectType, object_id: objectId, ...opts } }).then((r) => r.data);

export const efAdminLifecycleTransition = (token, payload, userToken = null) =>
  adminClient(token, userToken).post("/admin/epstein/lifecycle/transition", payload).then((r) => r.data);

export const efAdminLifecycleBatchTransition = (token, payload, userToken = null) =>
  adminClient(token, userToken).post("/admin/epstein/lifecycle/batch-transition", payload).then((r) => r.data);

export const efAdminLifecycleList = (token, params = {}, userToken = null) =>
  adminClient(token, userToken).get("/admin/epstein/lifecycle/list", { params }).then((r) => r.data);

export const efAdminLifecycleAudit = (token, params = {}, userToken = null) =>
  adminClient(token, userToken).get("/admin/epstein/lifecycle/audit", { params }).then((r) => r.data);

// V2: bulk, quick-filter, blast-radius
export const efAdminLifecycleBulkTransition = (token, payload, userToken = null) =>
  adminClient(token, userToken).post("/admin/epstein/lifecycle/bulk-transition", payload).then((r) => r.data);

export const efAdminLifecycleQuickFilter = (token, key, opts = {}, userToken = null) =>
  adminClient(token, userToken).get("/admin/epstein/lifecycle/quick-filter",
    { params: { key, ...opts } }).then((r) => r.data);

export const efAdminLifecycleBlastRadius = (token, objectType, objectId, userToken = null) =>
  adminClient(token, userToken).get(`/admin/epstein/lifecycle/blast-radius/${objectType}/${objectId}`)
    .then((r) => r.data);

// V3 — Source Reliability
export const efAdminSrList = (token, params = {}, userToken = null) =>
  adminClient(token, userToken).get("/admin/epstein/source-reliability/sources", { params }).then((r) => r.data);

export const efAdminSrDetail = (token, sourceId, userToken = null) =>
  adminClient(token, userToken).get(`/admin/epstein/source-reliability/source/${sourceId}`).then((r) => r.data);

export const efAdminSrPatch = (token, sourceId, payload, userToken = null) =>
  adminClient(token, userToken).patch(`/admin/epstein/source-reliability/source/${sourceId}`, payload).then((r) => r.data);

export const efAdminSrAttachReplacement = (token, payload, userToken = null) =>
  adminClient(token, userToken).post("/admin/epstein/source-reliability/attach-replacement", payload).then((r) => r.data);

export const efAdminSrBlocked = (token, userToken = null) =>
  adminClient(token, userToken).get("/admin/epstein/source-reliability/blocked-items").then((r) => r.data);

export const efAdminSrQuickFilter = (token, key, userToken = null) =>
  adminClient(token, userToken).get("/admin/epstein/source-reliability/quick-filter", { params: { key } }).then((r) => r.data);

// V4B — AI Intake / Deputy
export const efAdminIntakeStats = (token, userToken = null) =>
  adminClient(token, userToken).get("/admin/epstein/ai-intake/stats").then((r) => r.data);

export const efAdminIntakeIngestUrl = (token, payload, userToken = null) =>
  adminClient(token, userToken).post("/admin/epstein/ai-intake/ingest-url", payload).then((r) => r.data);

export const efAdminIntakeListJobs = (token, params = {}, userToken = null) =>
  adminClient(token, userToken).get("/admin/epstein/ai-intake/jobs", { params }).then((r) => r.data);

export const efAdminIntakeJobDetail = (token, jobId, userToken = null) =>
  adminClient(token, userToken).get(`/admin/epstein/ai-intake/job/${jobId}`).then((r) => r.data);

export const efAdminIntakeListCandidates = (token, params = {}, userToken = null) =>
  adminClient(token, userToken).get("/admin/epstein/ai-intake/candidates", { params }).then((r) => r.data);

export const efAdminIntakeCandidateDetail = (token, candidateId, userToken = null) =>
  adminClient(token, userToken).get(`/admin/epstein/ai-intake/candidate/${candidateId}`).then((r) => r.data);

export const efAdminIntakePatchCandidate = (token, candidateId, patch, userToken = null) =>
  adminClient(token, userToken).patch(`/admin/epstein/ai-intake/candidate/${candidateId}`, patch).then((r) => r.data);

export const efAdminIntakePromote = (token, candidateId, payload, userToken = null) =>
  adminClient(token, userToken).post(`/admin/epstein/ai-intake/candidate/${candidateId}/promote`, payload).then((r) => r.data);

export const efAdminIntakeReject = (token, candidateId, payload, userToken = null) =>
  adminClient(token, userToken).post(`/admin/epstein/ai-intake/candidate/${candidateId}/reject`, payload).then((r) => r.data);

export const efAdminIntakeAudit = (token, params = {}, userToken = null) =>
  adminClient(token, userToken).get("/admin/epstein/ai-intake/audit", { params }).then((r) => r.data);

// ============================================================================
// Dossier (long-form structured pages attached to a place / property entity)
// ============================================================================
export const epsteinPlaceDossier = (slug) =>
  client.get(`/epstein/places/${slug}`).then((r) => r.data);

export const efAdminDossierDetail = (token, slug, userToken = null) =>
  adminClient(token, userToken).get(`/admin/epstein/dossier/${slug}`).then((r) => r.data);

export const efAdminDossierSectionPatch = (token, slug, idx, payload, userToken = null) =>
  adminClient(token, userToken)
    .patch(`/admin/epstein/dossier/${slug}/section/${idx}`, payload)
    .then((r) => r.data);

export const efAdminEntityPatch = (token, eid, payload, userToken = null) =>
  adminClient(token, userToken)
    .patch(`/admin/epstein/entities/${eid}`, payload)
    .then((r) => r.data);



// ============================================================================
// TEMPORARY — Batch 1 production import (remove after import is complete)
// ============================================================================
export const efAdminBatch1DryRun = (token, userToken = null) =>
  adminClient(token, userToken).post("/admin/epstein/batch1/dry-run").then((r) => r.data);

export const efAdminBatch1Apply = (token, body, userToken = null) =>
  adminClient(token, userToken).post("/admin/epstein/batch1/apply", body).then((r) => r.data);

// TEMPORARY — Epstein cleanup diagnostics
export const efAdminCleanupImportsStale = (token, u = null) =>
  adminClient(token, u).get("/admin/epstein/cleanup/imports-stale").then(r => r.data);
export const efAdminCleanupImportsArchive = (token, body, u = null) =>
  adminClient(token, u).post("/admin/epstein/cleanup/imports-archive", body).then(r => r.data);
export const efAdminCleanupNonPublic = (token, u = null) =>
  adminClient(token, u).get("/admin/epstein/cleanup/non-public-entities").then(r => r.data);
export const efAdminCleanupBundleCoverage = (token, batch = null, u = null) =>
  adminClient(token, u).get("/admin/epstein/cleanup/bundle-coverage", { params: batch ? { batch } : {} }).then(r => r.data);
export const efAdminCleanupAnchorStatus = (token, u = null) =>
  adminClient(token, u).get("/admin/epstein/cleanup/anchor-status").then(r => r.data);
export const efAdminCleanupAnchorSetImage = (token, body, u = null) =>
  adminClient(token, u).post("/admin/epstein/cleanup/anchor-set-image", body).then(r => r.data);
export const efAdminCleanupSourceAttachments = (token, u = null) =>
  adminClient(token, u).get("/admin/epstein/cleanup/source-attachments").then(r => r.data);
// ============================================================================

// ---- Public audience endpoints ----
export const subscribeToArchive = (data) =>
  client.post("/analytics/subscribe", data).then((r) => r.data);
export const unsubscribeFromArchive = (token) =>
  client.get("/analytics/unsubscribe", { params: { token } }).then((r) => r.data);

// ---- Auth-gated user calls (Bearer token) ----
const userClient = (token) => axios.create({
  baseURL: API,
  timeout: 15000,
  headers: token ? { Authorization: `Bearer ${token}` } : {},
});

export const fetchLikes = (slug, token = null) =>
  userClient(token).get(`/topics/${slug}/likes`).then((r) => r.data);
export const toggleLike = (slug, token) =>
  userClient(token).post(`/topics/${slug}/like`).then((r) => r.data);

export const voteTopic = (id, direction, token) =>
  userClient(token).post(`/topics/${id}/vote`, { direction }).then((r) => r.data);
export const fetchVoteState = (id, token = null) =>
  userClient(token).get(`/topics/${id}/vote`).then((r) => r.data);

export const fetchTopicComments = (slug, limit = 50, token = null) =>
  userClient(token).get(`/topics/${slug}/comments`, { params: { limit } }).then((r) => r.data);
export const createTopicComment = (slug, body, token) =>
  userClient(token).post(`/topics/${slug}/comments`, { body }).then((r) => r.data);
export const editComment = (cid, body, token) =>
  userClient(token).patch(`/comments/${cid}`, { body }).then((r) => r.data);
export const deleteComment = (cid, token) =>
  userClient(token).delete(`/comments/${cid}`).then((r) => r.data);
export const reportComment = (cid, reason, token) =>
  userClient(token).post(`/comments/${cid}/report`, { reason: reason || null }).then((r) => r.data);

// ---- Admin comment moderation ----
export const adminListComments = (token, opts = {}, userToken = null) =>
  adminClient(token, userToken).get("/admin/comments", {
    params: { ...opts, ...(token ? { "x-admin-token": token } : {}) },
  }).then((r) => r.data);
export const adminRemoveComment = (token, cid, userToken = null, reason = null) =>
  adminClient(token, userToken).post(`/admin/comments/${cid}/remove`, { reason },
    { params: token ? { "x-admin-token": token } : {} }).then((r) => r.data);
export const adminHideComment = (token, cid, userToken = null, reason = null) =>
  adminClient(token, userToken).post(`/admin/comments/${cid}/hide`, { reason },
    { params: token ? { "x-admin-token": token } : {} }).then((r) => r.data);
export const adminSpamComment = (token, cid, userToken = null, reason = null) =>
  adminClient(token, userToken).post(`/admin/comments/${cid}/spam`, { reason },
    { params: token ? { "x-admin-token": token } : {} }).then((r) => r.data);
export const adminApproveComment = (token, cid, userToken = null) =>
  adminClient(token, userToken).post(`/admin/comments/${cid}/approve`, {},
    { params: token ? { "x-admin-token": token } : {} }).then((r) => r.data);
export const adminRestoreComment = (token, cid, userToken = null) =>
  adminClient(token, userToken).post(`/admin/comments/${cid}/restore`, {},
    { params: token ? { "x-admin-token": token } : {} }).then((r) => r.data);
export const adminBulkComments = (token, ids, action, userToken = null, reason = null) =>
  adminClient(token, userToken).post("/admin/comments/bulk-action",
    { ids, action, reason },
    { params: token ? { "x-admin-token": token } : {} }).then((r) => r.data);

// ---- Admin subscribers list ----
export const adminListSubscribers = (token, opts = {}, userToken = null) =>
  adminClient(token, userToken).get("/admin/subscribers", {
    params: { ...opts, ...(token ? { "x-admin-token": token } : {}) },
  }).then((r) => r.data);

export const adminSubscribersCsvUrl = (token, opts = {}) => {
  const u = new URL(`${API}/admin/subscribers.csv`);
  Object.entries(opts || {}).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== "") u.searchParams.set(k, v);
  });
  if (token) u.searchParams.set("x-admin-token", token);
  return u.toString();
};

// ---- Admin source integrity ----
export const adminSourcesHealth = (token, opts = {}, userToken = null) =>
  adminClient(token, userToken).get("/admin/sources/health", {
    params: { ...opts, ...(token ? { "x-admin-token": token } : {}) },
  }).then((r) => r.data);
export const adminSourcesAudit = (token, payload = {}, userToken = null) =>
  adminClient(token, userToken).post("/admin/sources/audit", payload,
    { params: token ? { "x-admin-token": token } : {} }).then((r) => r.data);
export const adminSourceRecheck = (token, sourceId, userToken = null) =>
  adminClient(token, userToken).post(`/admin/sources/${sourceId}/recheck`, {},
    { params: token ? { "x-admin-token": token } : {} }).then((r) => r.data);
export const adminSourceMarkReview = (token, sourceId, status, note = null, userToken = null) =>
  adminClient(token, userToken).post(`/admin/sources/${sourceId}/mark`,
    { status, note },
    { params: token ? { "x-admin-token": token } : {} }).then((r) => r.data);

// P1 — Manual Edit Source / Replace URL
export const adminSourceDependencies = (token, sourceId, userToken = null) =>
  adminClient(token, userToken).get(`/admin/sources/${sourceId}/dependencies`,
    { params: token ? { "x-admin-token": token } : {} }).then((r) => r.data);
export const adminSourceEdit = (token, sourceId, payload, userToken = null) =>
  adminClient(token, userToken).patch(`/admin/sources/${sourceId}`, payload,
    { params: token ? { "x-admin-token": token } : {} }).then((r) => r.data);

// ===== Epstein Files V1 =====
// Public
export const epsteinStats = () =>
  client.get("/epstein/stats").then((r) => r.data);
export const epsteinGraph = (view = "board") =>
  client.get("/epstein/graph", { params: { view } }).then((r) => r.data);
export const epsteinEntity = (slug) =>
  client.get(`/epstein/entity/${encodeURIComponent(slug)}`).then((r) => r.data);
export const epsteinSource = (sid) =>
  client.get(`/epstein/source/${encodeURIComponent(sid)}`).then((r) => r.data);
export const epsteinTimeline = () =>
  client.get("/epstein/timeline").then((r) => r.data);
export const epsteinTimelineEvent = (slug) =>
  client.get(`/epstein/timeline/${encodeURIComponent(slug)}`).then((r) => r.data);
export const epsteinEvidence = (slug) =>
  client.get(`/epstein/evidence/${encodeURIComponent(slug)}`).then((r) => r.data);
export const epsteinEvidenceList = () =>
  client.get("/epstein/evidence").then((r) => r.data);
export const epsteinSubmit = (payload) =>
  client.post("/epstein/submit", payload).then((r) => r.data);
export const epsteinSiteNotice = () =>
  client.get("/site/notice").then((r) => r.data);

// Admin
export const efAdminSettings = (token, userToken = null) =>
  adminClient(token, userToken).get("/admin/epstein/settings",
    { params: token ? { "x-admin-token": token } : {} }).then((r) => r.data);
export const efAdminUpdateSettings = (token, payload, userToken = null) =>
  adminClient(token, userToken).patch("/admin/epstein/settings", payload,
    { params: token ? { "x-admin-token": token } : {} }).then((r) => r.data);
export const efAdminEnableZorroRanchStub = (token, userToken = null) =>
  adminClient(token, userToken).post("/admin/epstein/zorro-ranch/enable-stub",
    null, { params: token ? { "x-admin-token": token } : {} }).then((r) => r.data);
export const efAdminSubmissions = (token, status = "pending", userToken = null) =>
  adminClient(token, userToken).get("/admin/epstein/submissions",
    { params: { status, ...(token ? { "x-admin-token": token } : {}) } }).then((r) => r.data);
export const efAdminSubmission = (token, sid, userToken = null) =>
  adminClient(token, userToken).get(`/admin/epstein/submissions/${sid}`,
    { params: token ? { "x-admin-token": token } : {} }).then((r) => r.data);
export const efAdminAttachSource = (token, sid, payload, userToken = null) =>
  adminClient(token, userToken).post(`/admin/epstein/submissions/${sid}/attach-source`, payload,
    { params: token ? { "x-admin-token": token } : {} }).then((r) => r.data);
export const efAdminDecide = (token, sid, payload, userToken = null) =>
  adminClient(token, userToken).post(`/admin/epstein/submissions/${sid}/decision`, payload,
    { params: token ? { "x-admin-token": token } : {} }).then((r) => r.data);
export const efAdminListEntities = (token, userToken = null) =>
  adminClient(token, userToken).get("/admin/epstein/entities",
    { params: token ? { "x-admin-token": token } : {} }).then((r) => r.data);
export const efAdminCreateEntity = (token, payload, userToken = null) =>
  adminClient(token, userToken).post("/admin/epstein/entities", payload,
    { params: token ? { "x-admin-token": token } : {} }).then((r) => r.data);
export const efAdminPatchEntity = (token, eid, payload, userToken = null) =>
  adminClient(token, userToken).patch(`/admin/epstein/entities/${eid}`, payload,
    { params: token ? { "x-admin-token": token } : {} }).then((r) => r.data);
export const efAdminEntityRouteDiagnostic = (token, eid, userToken = null) =>
  adminClient(token, userToken).get(`/admin/epstein/entities/${eid}/route-diagnostic`,
    { params: token ? { "x-admin-token": token } : {} }).then((r) => r.data);

// ===== Shared Dossier Visibility / Pullback =====
export const adminDossiersList = (token, params = {}, userToken = null) =>
  adminClient(token, userToken).get("/admin/dossiers", {
    params: { ...params, ...(token ? { "x-admin-token": token } : {}) },
  }).then((r) => r.data);
export const adminDossierRouteDiagnostic = (token, dossierType, dossierId, userToken = null) =>
  adminClient(token, userToken).get(
    `/admin/dossiers/${dossierType}/${dossierId}/route-diagnostic`,
    { params: token ? { "x-admin-token": token } : {} },
  ).then((r) => r.data);
export const adminDossierSetVisibility = (token, dossierType, dossierId, payload, userToken = null) =>
  adminClient(token, userToken).patch(
    `/admin/dossiers/${dossierType}/${dossierId}/visibility`,
    payload,
    { params: token ? { "x-admin-token": token } : {} },
  ).then((r) => r.data);
// Hero candidates + history (non-destructive hero workbench)
export const heroCandidates = (token, slug, userToken = null) =>
  adminClient(token, userToken).get(`/admin/heroes/candidates/${slug}`).then((r) => r.data);
export const heroCandidateGenerate = (token, slug, payload = {}, userToken = null) =>
  adminClient(token, userToken).post(`/admin/heroes/candidates/${slug}/generate`, payload).then((r) => r.data);
export const heroCandidateSelect = (token, slug, file, userToken = null) =>
  adminClient(token, userToken).post(`/admin/heroes/candidates/${slug}/select`, { file }).then((r) => r.data);
export const heroCandidateDiscard = (token, slug, file, userToken = null) =>
  adminClient(token, userToken).post(`/admin/heroes/candidates/${slug}/discard`, { file }).then((r) => r.data);
export const heroCandidateUpload = (token, slug, file, userToken = null) => {
  const fd = new FormData();
  fd.append("file", file);
  return adminClient(token, userToken).post(`/admin/heroes/candidates/${slug}/upload`, fd,
    { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
};
export const heroHistory = (token, slug, userToken = null) =>
  adminClient(token, userToken).get(`/admin/heroes/history/${slug}`).then((r) => r.data);
export const heroHistoryRestore = (token, slug, file, userToken = null) =>
  adminClient(token, userToken).post(`/admin/heroes/history/${slug}/restore`, { file }).then((r) => r.data);

export const adminDossierReasons = (token, userToken = null) =>
  adminClient(token, userToken).get("/admin/dossiers/reasons",
    { params: token ? { "x-admin-token": token } : {} }).then((r) => r.data);

// ===== Membership / Asset Directory =====
export const adminMembersList = (token, params = {}, userToken = null) =>
  adminClient(token, userToken).get("/admin/members", {
    params: { ...params, ...(token ? { "x-admin-token": token } : {}) },
  }).then((r) => r.data);
export const adminMemberDossier = (token, mid, userToken = null) =>
  adminClient(token, userToken).get(`/admin/members/${mid}`,
    { params: token ? { "x-admin-token": token } : {} }).then((r) => r.data);
export const adminMemberPatchNotes = (token, mid, notes, userToken = null) =>
  adminClient(token, userToken).patch(`/admin/members/${mid}/notes`,
    { admin_notes: notes },
    { params: token ? { "x-admin-token": token } : {} }).then((r) => r.data);
export const adminMemberPatch = (token, mid, payload, userToken = null) =>
  adminClient(token, userToken).patch(`/admin/members/${mid}`, payload,
    { params: token ? { "x-admin-token": token } : {} }).then((r) => r.data);
export const adminMemberRegenerateCallsign = (token, mid, userToken = null) =>
  adminClient(token, userToken).post(`/admin/members/${mid}/regenerate-callsign`, null,
    { params: token ? { "x-admin-token": token } : {} }).then((r) => r.data);
export const adminMemberExport = (token, mid, userToken = null) =>
  adminClient(token, userToken).get(`/admin/members/${mid}/export`,
    { params: token ? { "x-admin-token": token } : {} }).then((r) => r.data);
export const adminMemberDesignations = (token, userToken = null) =>
  adminClient(token, userToken).get("/admin/members/designations",
    { params: token ? { "x-admin-token": token } : {} }).then((r) => r.data);
export const adminMemberSendDossierUpdate = (token, mid, payload, userToken = null) =>
  adminClient(token, userToken).post(`/admin/members/${mid}/send-dossier-update`, payload,
    { params: token ? { "x-admin-token": token } : {} }).then((r) => r.data);
export const adminMemberSendEmail = (token, mid, subject, body, userToken = null) =>
  adminClient(token, userToken).post(`/admin/members/${mid}/send-email`,
    { subject, body },
    { params: token ? { "x-admin-token": token } : {} }).then((r) => r.data);
export const adminMemberMessages = (token, mid, userToken = null) =>
  adminClient(token, userToken).get(`/admin/members/${mid}/messages`,
    { params: token ? { "x-admin-token": token } : {} }).then((r) => r.data);
export const adminMemberAudit = (token, mid, limit = 50, userToken = null) =>
  adminClient(token, userToken).get(`/admin/members/${mid}/audit`,
    { params: { limit, ...(token ? { "x-admin-token": token } : {}) } }).then((r) => r.data);
export const adminMemberReservedCallsigns = (token, userToken = null) =>
  adminClient(token, userToken).get("/admin/members/reserved-callsigns",
    { params: token ? { "x-admin-token": token } : {} }).then((r) => r.data);
export const adminMemberArchive = (token, mid, reason, userToken = null) =>
  adminClient(token, userToken).post(`/admin/members/${mid}/archive`,
    { reason: reason || null },
    { params: token ? { "x-admin-token": token } : {} }).then((r) => r.data);
export const adminMemberRestore = (token, mid, userToken = null) =>
  adminClient(token, userToken).post(`/admin/members/${mid}/restore`, null,
    { params: token ? { "x-admin-token": token } : {} }).then((r) => r.data);
export const adminMemberDelete = (token, mid, payload, userToken = null) =>
  adminClient(token, userToken).delete(`/admin/members/${mid}`,
    { data: payload, params: token ? { "x-admin-token": token } : {} }
  ).then((r) => r.data);

// ===== Pre-Publish Review =====
export const adminDossierPreview = (token, dtype, did, userToken = null) =>
  adminClient(token, userToken).get(`/admin/dossiers/${dtype}/${did}/preview`,
    { params: token ? { "x-admin-token": token } : {} }).then((r) => r.data);
export const adminDossierSourceHealth = (token, dtype, did, userToken = null) =>
  adminClient(token, userToken).post(`/admin/dossiers/${dtype}/${did}/source-health`, null,
    { params: token ? { "x-admin-token": token } : {} }).then((r) => r.data);
export const adminDossierMatrix = (token, dtype, did, userToken = null) =>
  adminClient(token, userToken).get(`/admin/dossiers/${dtype}/${did}/evidence-matrix`,
    { params: token ? { "x-admin-token": token } : {} }).then((r) => r.data);
export const adminDossierSetClaimSupport = (token, dtype, did, payload, userToken = null) =>
  adminClient(token, userToken).patch(`/admin/dossiers/${dtype}/${did}/claim-support`, payload,
    { params: token ? { "x-admin-token": token } : {} }).then((r) => r.data);
export const adminDossierPreviewAck = (token, dtype, did, userToken = null) =>
  adminClient(token, userToken).post(`/admin/dossiers/${dtype}/${did}/preview-ack`, null,
    { params: token ? { "x-admin-token": token } : {} }).then((r) => r.data);
export const adminDossierPublishGate = (token, dtype, did, userToken = null) =>
  adminClient(token, userToken).post(`/admin/dossiers/${dtype}/${did}/publish-gate`, null,
    { params: token ? { "x-admin-token": token } : {} }).then((r) => r.data);
export const adminDossierPublish = (token, dtype, did, payload, userToken = null) =>
  adminClient(token, userToken).post(`/admin/dossiers/${dtype}/${did}/publish`, payload || {},
    { params: token ? { "x-admin-token": token } : {} }).then((r) => r.data);

// ===== Email Delivery (Phase B) =====
export const adminEmailHealth = (token, userToken = null) =>
  adminClient(token, userToken).get("/admin/email/health",
    { params: token ? { "x-admin-token": token } : {} }).then((r) => r.data);
export const adminEmailRefreshDomain = (token, userToken = null) =>
  adminClient(token, userToken).post("/admin/email/refresh-domain", null,
    { params: token ? { "x-admin-token": token } : {} }).then((r) => r.data);
export const adminMemberEmailHistory = (token, mid, userToken = null) =>
  adminClient(token, userToken).get(`/admin/members/${mid}/email-history`,
    { params: token ? { "x-admin-token": token } : {} }).then((r) => r.data);
export const adminEmailRetry = (token, messageId, userToken = null) =>
  adminClient(token, userToken).post(`/admin/email/${messageId}/retry`, null,
    { params: token ? { "x-admin-token": token } : {} }).then((r) => r.data);

// ===== Email Hub (central comms) =====
export const adminEmailHubDashboard = (token, userToken = null) =>
  adminClient(token, userToken).get("/admin/email/hub/dashboard",
    { params: token ? { "x-admin-token": token } : {} }).then((r) => r.data);
export const adminEmailHubRecentSends = (token, params = {}, userToken = null) =>
  adminClient(token, userToken).get("/admin/email/hub/recent-sends",
    { params: { ...(token ? { "x-admin-token": token } : {}), ...params } }).then((r) => r.data);
export const adminEmailHubTemplatesList = (token, userToken = null) =>
  adminClient(token, userToken).get("/admin/email/hub/templates",
    { params: token ? { "x-admin-token": token } : {} }).then((r) => r.data);
export const adminEmailHubTemplateCreate = (token, payload, userToken = null) =>
  adminClient(token, userToken).post("/admin/email/hub/templates", payload,
    { params: token ? { "x-admin-token": token } : {} }).then((r) => r.data);
export const adminEmailHubTemplateUpdate = (token, tid, payload, userToken = null) =>
  adminClient(token, userToken).put(`/admin/email/hub/templates/${tid}`, payload,
    { params: token ? { "x-admin-token": token } : {} }).then((r) => r.data);
export const adminEmailHubTemplateDelete = (token, tid, userToken = null) =>
  adminClient(token, userToken).delete(`/admin/email/hub/templates/${tid}`,
    { params: token ? { "x-admin-token": token } : {} }).then((r) => r.data);
export const adminEmailHubDraftsList = (token, userToken = null) =>
  adminClient(token, userToken).get("/admin/email/hub/drafts",
    { params: token ? { "x-admin-token": token } : {} }).then((r) => r.data);
export const adminEmailHubDraftCreate = (token, payload, userToken = null) =>
  adminClient(token, userToken).post("/admin/email/hub/drafts", payload,
    { params: token ? { "x-admin-token": token } : {} }).then((r) => r.data);
export const adminEmailHubDraftUpdate = (token, did, payload, userToken = null) =>
  adminClient(token, userToken).put(`/admin/email/hub/drafts/${did}`, payload,
    { params: token ? { "x-admin-token": token } : {} }).then((r) => r.data);
export const adminEmailHubDraftDelete = (token, did, userToken = null) =>
  adminClient(token, userToken).delete(`/admin/email/hub/drafts/${did}`,
    { params: token ? { "x-admin-token": token } : {} }).then((r) => r.data);
export const adminEmailHubAudiencePreview = (token, filter, userToken = null) =>
  adminClient(token, userToken).post("/admin/email/hub/audience-preview", filter,
    { params: token ? { "x-admin-token": token } : {} }).then((r) => r.data);
export const adminEmailHubSendBulk = (token, payload, userToken = null) =>
  adminClient(token, userToken).post("/admin/email/hub/send-bulk", payload,
    { params: token ? { "x-admin-token": token } : {} }).then((r) => r.data);
export const adminEmailHubSendTest = (token, payload, userToken = null) =>
  adminClient(token, userToken).post("/admin/email/hub/send-test", payload,
    { params: token ? { "x-admin-token": token } : {} }).then((r) => r.data);
export const adminEmailHubMemberSearch = (token, q, userToken = null) =>
  adminClient(token, userToken).get("/admin/email/hub/member-search",
    { params: { ...(token ? { "x-admin-token": token } : {}), q, limit: 12 } }).then((r) => r.data);

// ===== Bulk Member Operations =====
export const adminMembersBulkTag = (token, payload, userToken = null) =>
  adminClient(token, userToken).post("/admin/members/bulk-tag", payload,
    { params: token ? { "x-admin-token": token } : {} }).then((r) => r.data);
export const adminMembersBulkClearance = (token, payload, userToken = null) =>
  adminClient(token, userToken).post("/admin/members/bulk-clearance", payload,
    { params: token ? { "x-admin-token": token } : {} }).then((r) => r.data);
export const adminMembersBulkNewsletter = (token, payload, userToken = null) =>
  adminClient(token, userToken).post("/admin/members/bulk-newsletter", payload,
    { params: token ? { "x-admin-token": token } : {} }).then((r) => r.data);
export const adminMembersBulkArchive = (token, payload, userToken = null) =>
  adminClient(token, userToken).post("/admin/members/bulk-archive", payload,
    { params: token ? { "x-admin-token": token } : {} }).then((r) => r.data);
export const adminMembersBulkRestore = (token, payload, userToken = null) =>
  adminClient(token, userToken).post("/admin/members/bulk-restore", payload,
    { params: token ? { "x-admin-token": token } : {} }).then((r) => r.data);

// CSV export — returns a URL the user can click. We append the token as a query
// param so a direct browser-initiated download works (browsers do not let us
// add auth headers to a plain anchor tag).
export const adminMembersExportCsvUrl = (token, filters = {}) => {
  const params = new URLSearchParams({
    ...(token ? { "x-admin-token": token } : {}),
    ...Object.fromEntries(Object.entries(filters).filter(([_, v]) => v != null && v !== "")),
  }).toString();
  const base = process.env.REACT_APP_BACKEND_URL || "";
  return `${base}/api/admin/members/export.csv?${params}`;
};

export const adminNewsletterQueue = (token, payload, userToken = null) =>
  adminClient(token, userToken).post("/admin/newsletter/queue", payload,
    { params: token ? { "x-admin-token": token } : {} }).then((r) => r.data);
export const adminNewsletterList = (token, userToken = null) =>
  adminClient(token, userToken).get("/admin/newsletter/queue",
    { params: token ? { "x-admin-token": token } : {} }).then((r) => r.data);
export const adminNewsletterPreview = (token, nid, userToken = null) =>
  adminClient(token, userToken).post(`/admin/newsletter/${nid}/preview`, null,
    { params: token ? { "x-admin-token": token } : {} }).then((r) => r.data);
export const adminNewsletterConfirmSend = (token, nid, userToken = null) =>
  adminClient(token, userToken).post(`/admin/newsletter/${nid}/confirm-send`, null,
    { params: token ? { "x-admin-token": token } : {} }).then((r) => r.data);
export const efAdminCreateRelationship = (token, payload, userToken = null) =>
  adminClient(token, userToken).post("/admin/epstein/relationships", payload,
    { params: token ? { "x-admin-token": token } : {} }).then((r) => r.data);
export const efAdminSeedDemo = (token, userToken = null) =>
  adminClient(token, userToken).post("/admin/epstein/seed-demo", {},
    { params: token ? { "x-admin-token": token } : {} }).then((r) => r.data);

// ===== Privacy Maintenance (Owner only) =====
export const adminPrivacyAudit = (token, userToken = null) =>
  adminClient(token, userToken).get("/admin/privacy/audit",
    { params: token ? { "x-admin-token": token } : {} }).then((r) => r.data);
export const adminPrivacyScrub = (token, payload, userToken = null) =>
  adminClient(token, userToken).post("/admin/privacy/scrub", payload,
    { params: token ? { "x-admin-token": token } : {} }).then((r) => r.data);


// =====================================================================
// Archive Dispatch (member-to-member messaging)
// =====================================================================

const _memberClient = (memberToken) =>
  attachSessionExpiryInterceptor(axios.create({
    baseURL: API,
    timeout: API_TIMEOUT_MS,
    headers: memberToken ? { Authorization: `Bearer ${memberToken}` } : {},
  }));

export const dispatchGetPrefs = (memberToken) =>
  _memberClient(memberToken).get("/dispatch/preferences").then((r) => r.data);
export const dispatchSetPrefs = (memberToken, payload) =>
  _memberClient(memberToken).patch("/dispatch/preferences", payload).then((r) => r.data);
export const dispatchMemberSearch = (memberToken, q) =>
  _memberClient(memberToken).get("/dispatch/member-search",
    { params: { q, limit: 12 } }).then((r) => r.data);
export const dispatchContactAdmin = (memberToken, payload) =>
  _memberClient(memberToken).post("/dispatch/contact-admin", payload).then((r) => r.data);
export const dispatchSend = (memberToken, payload) =>
  _memberClient(memberToken).post("/dispatch/send", payload).then((r) => r.data);
export const dispatchInbox = (memberToken, params = {}) =>
  _memberClient(memberToken).get("/dispatch/inbox", { params }).then((r) => r.data);
export const dispatchSent = (memberToken, params = {}) =>
  _memberClient(memberToken).get("/dispatch/sent", { params }).then((r) => r.data);
export const dispatchArchived = (memberToken, params = {}) =>
  _memberClient(memberToken).get("/dispatch/archived", { params }).then((r) => r.data);
export const dispatchThread = (memberToken, tid, params = {}) =>
  _memberClient(memberToken).get(`/dispatch/threads/${tid}`, { params }).then((r) => r.data);
export const dispatchMarkRead = (memberToken, tid) =>
  _memberClient(memberToken).post(`/dispatch/threads/${tid}/read`).then((r) => r.data);
export const dispatchArchive = (memberToken, tid) =>
  _memberClient(memberToken).post(`/dispatch/threads/${tid}/archive`).then((r) => r.data);
export const dispatchUnarchive = (memberToken, tid) =>
  _memberClient(memberToken).post(`/dispatch/threads/${tid}/unarchive`).then((r) => r.data);
export const dispatchMute = (memberToken, tid) =>
  _memberClient(memberToken).post(`/dispatch/threads/${tid}/mute`).then((r) => r.data);
export const dispatchUnmute = (memberToken, tid) =>
  _memberClient(memberToken).post(`/dispatch/threads/${tid}/unmute`).then((r) => r.data);
export const dispatchBlock = (memberToken, mid) =>
  _memberClient(memberToken).post(`/dispatch/block/${mid}`).then((r) => r.data);
export const dispatchUnblock = (memberToken, mid) =>
  _memberClient(memberToken).post(`/dispatch/unblock/${mid}`).then((r) => r.data);
export const dispatchBlocksList = (memberToken) =>
  _memberClient(memberToken).get("/dispatch/blocks").then((r) => r.data);
export const dispatchReport = (memberToken, payload) =>
  _memberClient(memberToken).post("/dispatch/report", payload).then((r) => r.data);
export const dispatchUnreadCount = (memberToken) =>
  _memberClient(memberToken).get("/dispatch/unread-count").then((r) => r.data);

// Admin Dispatch
export const adminDispatchStats = (token, userToken = null) =>
  adminClient(token, userToken).get("/admin/dispatch/stats",
    { params: token ? { "x-admin-token": token } : {} }).then((r) => r.data);
export const adminDispatchReports = (token, params = {}, userToken = null) =>
  adminClient(token, userToken).get("/admin/dispatch/reports",
    { params: { ...(token ? { "x-admin-token": token } : {}), ...params } }).then((r) => r.data);
export const adminDispatchResolveReport = (token, rid, payload, userToken = null) =>
  adminClient(token, userToken).post(`/admin/dispatch/reports/${rid}/resolve`, payload,
    { params: token ? { "x-admin-token": token } : {} }).then((r) => r.data);
export const adminDispatchListThreads = (token, params = {}, userToken = null) =>
  adminClient(token, userToken).get("/admin/dispatch/threads",
    { params: { ...(token ? { "x-admin-token": token } : {}), ...params } }).then((r) => r.data);
export const adminDispatchThread = (token, tid, userToken = null) =>
  adminClient(token, userToken).get(`/admin/dispatch/threads/${tid}`,
    { params: token ? { "x-admin-token": token } : {} }).then((r) => r.data);
export const adminDispatchHideMessage = (token, mid, userToken = null) =>
  adminClient(token, userToken).post(`/admin/dispatch/messages/${mid}/hide`, {},
    { params: token ? { "x-admin-token": token } : {} }).then((r) => r.data);
export const adminDispatchBroadcast = (token, payload, userToken = null) =>
  adminClient(token, userToken).post("/admin/dispatch/broadcast", payload,
    { params: token ? { "x-admin-token": token } : {} }).then((r) => r.data);

// Import pipeline
export const efAdminImportTemplate = (token, userToken = null) =>
  adminClient(token, userToken).get("/admin/epstein/import-template",
    { params: token ? { "x-admin-token": token } : {} }).then((r) => r.data);

// ===== Member Operations Toolbar (dossier-level actions) =====
export const memberBookmarkAdd = (memberToken, slug) =>
  _memberClient(memberToken).post(`/members/me/bookmarks/${slug}`).then((r) => r.data);
export const memberBookmarkRemove = (memberToken, slug) =>
  _memberClient(memberToken).delete(`/members/me/bookmarks/${slug}`).then((r) => r.data);
export const memberFollow = (memberToken, slug) =>
  _memberClient(memberToken).post(`/members/me/follow/${slug}`).then((r) => r.data);
export const memberUnfollow = (memberToken, slug) =>
  _memberClient(memberToken).delete(`/members/me/follow/${slug}`).then((r) => r.data);
export const memberDossierState = (memberToken, slug) =>
  _memberClient(memberToken).get(`/members/me/dossier-state/${slug}`).then((r) => r.data);

export const efAdminImportBatch = (token, payload, userToken = null) =>
  adminClient(token, userToken).post("/admin/epstein/import", payload,
    { params: token ? { "x-admin-token": token } : {} }).then((r) => r.data);
export const efAdminListImports = (token, userToken = null) =>
  adminClient(token, userToken).get("/admin/epstein/imports",
    { params: token ? { "x-admin-token": token } : {} }).then((r) => r.data);
export const efAdminGetImport = (token, batchId, statusFilter = null, userToken = null) =>
  adminClient(token, userToken).get(`/admin/epstein/imports/${batchId}`,
    { params: { ...(statusFilter ? { status: statusFilter } : {}), ...(token ? { "x-admin-token": token } : {}) } }).then((r) => r.data);
export const efAdminDecideImportItem = (token, batchId, itemId, payload, userToken = null) =>
  adminClient(token, userToken).post(`/admin/epstein/imports/${batchId}/items/${itemId}/decision`, payload,
    { params: token ? { "x-admin-token": token } : {} }).then((r) => r.data);
export const efAdminApproveCleanImports = (token, batchId, userToken = null) =>
  adminClient(token, userToken).post(`/admin/epstein/imports/${batchId}/approve-clean`, {},
    { params: token ? { "x-admin-token": token } : {} }).then((r) => r.data);
export const efAdminBulkRejectImports = (token, batchId, itemIds, editorNote, userToken = null) =>
  adminClient(token, userToken).post(`/admin/epstein/imports/${batchId}/bulk-reject`,
    { item_ids: itemIds, editor_note: editorNote || null },
    { params: token ? { "x-admin-token": token } : {} }).then((r) => r.data);
export const efAdminPublishApprovedBatch = (token, batchId, userToken = null) =>
  adminClient(token, userToken).post(`/admin/epstein/imports/${batchId}/publish-approved`, {},
    { params: token ? { "x-admin-token": token } : {} }).then((r) => r.data);
export const efAdminRestoreBundledBatches = (token, userToken = null) =>
  adminClient(token, userToken).post("/admin/epstein/restore-bundled-batches", {},
    { params: token ? { "x-admin-token": token } : {}, timeout: 120000 }).then((r) => r.data);
export const efAdminPublishRothschildLane = (token, userToken = null) =>
  adminClient(token, userToken).post("/admin/epstein/publish-rothschild-lane", {},
    { params: token ? { "x-admin-token": token } : {}, timeout: 120000 }).then((r) => r.data);
export const efAdminPublishFirstMixedLane = (token, userToken = null) =>
  adminClient(token, userToken).post("/admin/epstein/publish-first-mixed-lane", {},
    { params: token ? { "x-admin-token": token } : {}, timeout: 120000 }).then((r) => r.data);
export const efAdminUploadEntityImage = (token, file, userToken = null, onProgress = null) => {
  const fd = new FormData();
  fd.append("file", file, file.name || "upload");
  return adminClient(token, userToken).post("/admin/epstein/upload-image", fd, {
    params: token ? { "x-admin-token": token } : {},
    headers: { "Content-Type": "multipart/form-data" },
    timeout: 90000,
    onUploadProgress: onProgress
      ? (evt) => {
          if (evt.total) onProgress(Math.round((evt.loaded / evt.total) * 100));
        }
      : undefined,
  }).then((r) => r.data);
};

export default client;

// ===== RBAC (Phase 2) =====
export const adminRbacMe = (token, userToken = null) =>
  adminClient(token, userToken).get("/admin/rbac/me",
    { params: token ? { "x-admin-token": token } : {} }).then((r) => r.data);
export const adminRbacRoles = (token, userToken = null) =>
  adminClient(token, userToken).get("/admin/rbac/roles",
    { params: token ? { "x-admin-token": token } : {} }).then((r) => r.data);
export const adminRbacPermissions = (token, userToken = null) =>
  adminClient(token, userToken).get("/admin/rbac/permissions",
    { params: token ? { "x-admin-token": token } : {} }).then((r) => r.data);
export const adminRbacUsers = (token, params = {}, userToken = null) =>
  adminClient(token, userToken).get("/admin/rbac/users", {
    params: { ...params, ...(token ? { "x-admin-token": token } : {}) },
  }).then((r) => r.data);
export const adminRbacPatchUser = (token, userId, payload, userToken = null) =>
  adminClient(token, userToken).patch(`/admin/rbac/users/${userId}`, payload,
    { params: token ? { "x-admin-token": token } : {} }).then((r) => r.data);
export const adminRbacAuditLog = (token, limit = 100, userToken = null) =>
  adminClient(token, userToken).get("/admin/rbac/audit-log",
    { params: { limit, ...(token ? { "x-admin-token": token } : {}) } }).then((r) => r.data);
export const adminRbacCheck = (token, perm, userToken = null) =>
  adminClient(token, userToken).get("/admin/rbac/check",
    { params: { perm, ...(token ? { "x-admin-token": token } : {}) } }).then((r) => r.data);

// ===== Editorial Standard v1 =====
export const fetchEditorialModules = (slug) =>
  client.get(`/topics/${encodeURIComponent(slug)}/editorial`).then((r) => r.data);
export const adminEditorialGenerate = (token, slug, userToken = null) =>
  adminClient(token, userToken).post(
    `/admin/editorial/topics/${encodeURIComponent(slug)}/generate`,
    {},
    { params: token ? { "x-admin-token": token } : {} },
  ).then((r) => r.data);
export const adminEditorialPatch = (token, slug, payload, userToken = null) =>
  adminClient(token, userToken).patch(
    `/admin/editorial/topics/${encodeURIComponent(slug)}/modules`, payload,
    { params: token ? { "x-admin-token": token } : {} },
  ).then((r) => r.data);


// ---- Admin Security (Login Hardening) — Owner-only ----
export const adminSecurityFailedAccess = (token, params = {}, userToken = null) =>
  adminClient(token, userToken).get("/admin/security/failed-access", {
    params: { ...params, ...(token ? { "x-admin-token": token } : {}) },
  }).then((r) => r.data);

export const adminSecurityStats = (token, userToken = null) =>
  adminClient(token, userToken).get("/admin/security/stats").then((r) => r.data);

export const adminSecurityLockouts = (token, activeOnly = false, userToken = null) =>
  adminClient(token, userToken).get("/admin/security/lockouts", {
    params: { active_only: activeOnly, ...(token ? { "x-admin-token": token } : {}) },
  }).then((r) => r.data);

export const adminSecurityReleaseLockout = (token, fingerprint, userToken = null) =>
  adminClient(token, userToken).post("/admin/security/lockouts/release",
    { fingerprint },
    { params: token ? { "x-admin-token": token } : {} },
  ).then((r) => r.data);

export const adminSecurityExportCsvUrl = (token) => {
  const base = `${API}/admin/security/export.csv`;
  if (!token) return base;
  return `${base}?x-admin-token=${encodeURIComponent(token)}`;
};

// ---- Permanent Block list (Owner-only) ----
export const adminSecurityListPermanentBlocks = (token, params = {}, userToken = null) =>
  adminClient(token, userToken).get("/admin/security/permanent-blocks", {
    params: { ...params, ...(token ? { "x-admin-token": token } : {}) },
  }).then((r) => r.data);

export const adminSecurityCreatePermanentBlock = (token, payload, userToken = null) =>
  adminClient(token, userToken).post("/admin/security/permanent-blocks", payload,
    { params: token ? { "x-admin-token": token } : {} },
  ).then((r) => r.data);

export const adminSecurityRemovePermanentBlock = (token, kind, value, userToken = null) =>
  adminClient(token, userToken).delete("/admin/security/permanent-blocks", {
    params: { kind, value, ...(token ? { "x-admin-token": token } : {}) },
  }).then((r) => r.data);

// ---- Mission Briefing (Owner-only) ----
export const adminCommandMissionBriefing = (token, forceRefresh = false, userToken = null) =>
  adminClient(token, userToken).post(
    "/admin/command/mission-briefing",
    { force_refresh: !!forceRefresh },
    { params: token ? { "x-admin-token": token } : {} },
  ).then((r) => r.data);

// ===== Hero Review queue (publish gate visibility) =====
const _heroParams = (token) => (token ? { "x-admin-token": token } : {});
export const adminHeroQueue = (token, userToken = null) =>
  adminClient(token, userToken).get("/admin/heroes/queue",
    { params: _heroParams(token) }).then((r) => r.data);
export const adminHeroGenerate = (token, slug, userToken = null) =>
  adminClient(token, userToken).post(`/admin/heroes/generate/${slug}`, {},
    { params: _heroParams(token) }).then((r) => r.data);
export const adminHeroStatus = (token, slug, userToken = null) =>
  adminClient(token, userToken).get(`/admin/heroes/status/${slug}`,
    { params: _heroParams(token) }).then((r) => r.data);
export const adminHeroVerify = (token, slug, userToken = null) =>
  adminClient(token, userToken).get(`/admin/heroes/verify/${slug}`,
    { params: _heroParams(token) }).then((r) => r.data);
export const adminHeroApprove = (token, slug, userToken = null) =>
  adminClient(token, userToken).post(`/admin/heroes/approve/${slug}`, {},
    { params: _heroParams(token) }).then((r) => r.data);
export const adminHeroKeepHeld = (token, slug, userToken = null) =>
  adminClient(token, userToken).post(`/admin/heroes/keep-held/${slug}`, {},
    { params: _heroParams(token) }).then((r) => r.data);
export const adminHeroAudit = (token, userToken = null, baseUrl = null) =>
  adminClient(token, userToken).get("/admin/heroes/audit",
    { params: { ..._heroParams(token), ...(baseUrl ? { base_url: baseUrl } : {}) } }).then((r) => r.data);
export const adminHeroAuditRun = (token, userToken = null) =>
  adminClient(token, userToken).post("/admin/heroes/audit/run", {},
    { params: _heroParams(token) }).then((r) => r.data);
export const adminHeroAuditLatest = (token, userToken = null) =>
  adminClient(token, userToken).get("/admin/heroes/audit/latest",
    { params: _heroParams(token) }).then((r) => r.data);

// ===== Source Health (source-backed guarantee) =====
export const adminSourceLinkHealth = (token, userToken = null) =>
  adminClient(token, userToken).get("/admin/source-links/health",
    { params: _heroParams(token) }).then((r) => r.data);
export const adminProfileReviewQueue = (token, userToken = null) =>
  adminClient(token, userToken).get("/admin/profile-review/queue",
    { params: _heroParams(token) }).then((r) => r.data);
export const adminProfileReviewReplaceSource = (token, payload, userToken = null) =>
  adminClient(token, userToken).post("/admin/profile-review/replace-source", payload,
    { params: _heroParams(token) }).then((r) => r.data);
export const adminProfileReviewRevalidate = (token, slug, userToken = null) =>
  adminClient(token, userToken).post("/admin/profile-review/revalidate", { slug },
    { params: _heroParams(token) }).then((r) => r.data);
export const adminNetworkHealth = (token, userToken = null) =>
  adminClient(token, userToken).get("/admin/network-health",
    { params: _heroParams(token) }).then((r) => r.data);
export const adminSourceLinkAuditRun = (token, userToken = null) =>
  adminClient(token, userToken).post("/admin/source-links/audit/run", {},
    { params: _heroParams(token) }).then((r) => r.data);
export const adminSourceLinkRecheck = (token, slug, url, userToken = null) =>
  adminClient(token, userToken).post("/admin/source-links/recheck", { slug, url },
    { params: _heroParams(token) }).then((r) => r.data);
export const adminSourceLinkReplace = (token, slug, url, newUrl, userToken = null) =>
  adminClient(token, userToken).post("/admin/source-links/replace",
    { slug, url, new_url: newUrl },
    { params: _heroParams(token) }).then((r) => r.data);
export const adminSourceLinkArchiveCopy = (token, slug, url, userToken = null) =>
  adminClient(token, userToken).post("/admin/source-links/archive-copy", { slug, url },
    { params: _heroParams(token) }).then((r) => r.data);
export const adminSourceLinkMark = (token, slug, url, mark, userToken = null) =>
  adminClient(token, userToken).post("/admin/source-links/mark", { slug, url, mark },
    { params: _heroParams(token) }).then((r) => r.data);
export const adminSourceLinkDecide = (token, slug, url, decision, userToken = null) =>
  adminClient(token, userToken).post("/admin/source-links/suggestion/decide",
    { slug, url, decision },
    { params: _heroParams(token) }).then((r) => r.data);

// ===== Evidence Recovery (editorial evidence-recovery workflow) =====
export const adminRecoveryQueue = (token, userToken = null) =>
  adminClient(token, userToken).get("/admin/evidence-recovery/queue",
    { params: _heroParams(token) }).then((r) => r.data);
export const adminRecoveryCheck = (token, slug, url, userToken = null) =>
  adminClient(token, userToken).post("/admin/evidence-recovery/check", { slug, url },
    { params: _heroParams(token) }).then((r) => r.data);
export const adminRecoveryApply = (token, payload, userToken = null) =>
  adminClient(token, userToken).post("/admin/evidence-recovery/apply", payload,
    { params: _heroParams(token) }).then((r) => r.data);

// ===== Oracle Retrieval Certification (operational health) =====
export const adminCertLatest = (token, userToken = null) =>
  adminClient(token, userToken).get("/admin/oracle/certification/latest",
    { params: _heroParams(token) }).then((r) => r.data);
export const adminCertReports = (token, days = 0, userToken = null) =>
  adminClient(token, userToken).get("/admin/oracle/certification/reports",
    { params: { ..._heroParams(token), ...(days ? { days } : {}) } }).then((r) => r.data);
export const adminCertRun = (token, userToken = null) =>
  adminClient(token, userToken).post("/admin/oracle/certification/run", {},
    { params: _heroParams(token) }).then((r) => r.data);
export const adminCertReport = (token, id, userToken = null) =>
  adminClient(token, userToken).get(`/admin/oracle/certification/report/${id}`,
    { params: _heroParams(token) }).then((r) => r.data);

// ===== Oracle Security Events (Founder-only privacy enforcement) =====
export const adminOracleSecurityEvents = (token, days = 7, userToken = null) =>
  adminClient(token, userToken).get("/oracle/console/security-events",
    { params: { ..._heroParams(token), days } }).then((r) => r.data);

// ===== Admin Media Library (mobile-first photo pipeline) =====
export const mediaList = (token, params = {}, userToken = null) =>
  adminClient(token, userToken).get("/admin/media",
    { params: { ...params, ...(token ? { "x-admin-token": token } : {}) } }).then((r) => r.data);
export const mediaGet = (token, id, userToken = null) =>
  adminClient(token, userToken).get(`/admin/media/${id}`,
    { params: token ? { "x-admin-token": token } : {} }).then((r) => r.data.asset);
export const mediaUpload = (token, file, batchId, onProgress, userToken = null) => {
  const fd = new FormData();
  fd.append("file", file, file.name || "photo");
  if (batchId) fd.append("batch_id", batchId);
  return adminClient(token, userToken).post("/admin/media/upload", fd, {
    params: token ? { "x-admin-token": token } : {},
    headers: { "Content-Type": "multipart/form-data" },
    onUploadProgress: (e) => {
      if (onProgress && e.total) onProgress(Math.round((e.loaded / e.total) * 100));
    },
  }).then((r) => r.data.asset);
};
export const mediaPatch = (token, id, body, userToken = null) =>
  adminClient(token, userToken).patch(`/admin/media/${id}`, body,
    { params: token ? { "x-admin-token": token } : {} }).then((r) => r.data.asset);
export const mediaAttach = (token, id, body, userToken = null) =>
  adminClient(token, userToken).post(`/admin/media/${id}/attach`, body,
    { params: token ? { "x-admin-token": token } : {} }).then((r) => r.data.asset);
export const mediaDetach = (token, id, body, userToken = null) =>
  adminClient(token, userToken).post(`/admin/media/${id}/detach`, body,
    { params: token ? { "x-admin-token": token } : {} }).then((r) => r.data.asset);
export const mediaAction = (token, id, action, userToken = null) =>
  adminClient(token, userToken).post(`/admin/media/${id}/${action}`, {},
    { params: token ? { "x-admin-token": token } : {} }).then((r) => r.data.asset);
export const mediaDelete = (token, id, userToken = null) =>
  adminClient(token, userToken).delete(`/admin/media/${id}`,
    { params: token ? { "x-admin-token": token } : {} }).then((r) => r.data);
// Admin media binaries — fetched through the SAME authenticated client as
// every other admin call (OAuth session Bearer and/or operator token).
// <img src> URLs cannot carry the session header, so we fetch blobs.
const _mediaBlobCache = new Map();
export const mediaBlobUrl = async (token, id, kind = "thumb_webp", userToken = null) => {
  const key = `${id}:${kind || "original"}`;
  if (_mediaBlobCache.has(key)) return _mediaBlobCache.get(key);
  const path = kind ? `/admin/media/${id}/derivative` : `/admin/media/${id}/original`;
  const r = await adminClient(token, userToken).get(path, {
    params: { ...(kind ? { kind } : {}), ...(token ? { "x-admin-token": token } : {}) },
    responseType: "blob",
  });
  const url = URL.createObjectURL(r.data);
  _mediaBlobCache.set(key, url);
  return url;
};
export const mediaExportCsv = async (token, userToken = null) => {
  const r = await adminClient(token, userToken).get("/admin/media/export.csv", {
    params: token ? { "x-admin-token": token } : {}, responseType: "blob" });
  return URL.createObjectURL(r.data);
};
export const mediaHealth = (token, userToken = null) =>
  adminClient(token, userToken).get("/admin/media/health",
    { params: token ? { "x-admin-token": token } : {} }).then((r) => r.data.counts);
export const mediaBulk = (token, body, userToken = null) =>
  adminClient(token, userToken).post("/admin/media/bulk", body,
    { params: token ? { "x-admin-token": token } : {} }).then((r) => r.data);
export const wikiReviewQueue = (token, userToken = null) =>
  adminClient(token, userToken).get("/admin/media/ingest/review-queue",
    { params: token ? { "x-admin-token": token } : {} }).then((r) => r.data);
export const wikiReviewDecide = (token, ids, action, userToken = null) =>
  adminClient(token, userToken).post("/admin/media/ingest/review-queue/decide",
    { ids, action },
    { params: token ? { "x-admin-token": token } : {} }).then((r) => r.data);
export const editorialWorkload = (token, userToken = null) =>
  adminClient(token, userToken).get("/admin/editorial/workload",
    { params: token ? { "x-admin-token": token } : {} }).then((r) => r.data);
export const editorialBulkSuggestions = (token, userToken = null) =>
  adminClient(token, userToken).get("/admin/editorial/bulk-suggestions",
    { params: token ? { "x-admin-token": token } : {} }).then((r) => r.data);
export const editorialDecide = (token, ids, action, reason = null, userToken = null) =>
  adminClient(token, userToken).post("/admin/editorial/decide", { ids, action, reason },
    { params: token ? { "x-admin-token": token } : {} }).then((r) => r.data);
export const editorialReadiness = (token, userToken = null) =>
  adminClient(token, userToken).get("/admin/editorial/readiness",
    { params: token ? { "x-admin-token": token } : {} }).then((r) => r.data);
export const editorialHistory = (token, userToken = null) =>
  adminClient(token, userToken).get("/admin/editorial/history",
    { params: token ? { "x-admin-token": token } : {} }).then((r) => r.data);
export const editorialRollback = (token, historyId, userToken = null) =>
  adminClient(token, userToken).post("/admin/editorial/rollback", { history_id: historyId },
    { params: token ? { "x-admin-token": token } : {} }).then((r) => r.data);
// Public — published assets attached to a record (no auth, published-only).
export const recordMedia = (targetType, targetId) =>
  client.get(`/media/for/${targetType}/${encodeURIComponent(targetId)}`)
    .then((r) => r.data.images || []);

// ===== OPERATION LOCKDOWN — Founder Step-Up Authentication =====
const _fs = (token, userToken) => adminClient(token, userToken);
const _fsp = (token) => (token ? { "x-admin-token": token } : {});
export const stepupStatus = (t, u = null) =>
  _fs(t, u).get("/admin/founder/stepup/status", { params: _fsp(t) }).then((r) => r.data);
export const stepupTotpBegin = (t, u = null) =>
  _fs(t, u).post("/admin/founder/stepup/totp/begin", {}, { params: _fsp(t) }).then((r) => r.data);
export const stepupTotpConfirm = (t, code, u = null) =>
  _fs(t, u).post("/admin/founder/stepup/totp/confirm", { code }, { params: _fsp(t) }).then((r) => r.data);
export const stepupVerify = (t, method, code, u = null) =>
  _fs(t, u).post("/admin/founder/stepup/verify", { method, code }, { params: _fsp(t) }).then((r) => r.data);
export const stepupRecoveryRegen = (t, u = null) =>
  _fs(t, u).post("/admin/founder/stepup/recovery/regenerate", {}, { params: _fsp(t) }).then((r) => r.data);
export const stepupWebauthnRegOptions = (t, u = null) =>
  _fs(t, u).post("/admin/founder/stepup/webauthn/register/options", {}, { params: _fsp(t) }).then((r) => r.data);
export const stepupWebauthnRegVerify = (t, credential, name, u = null) =>
  _fs(t, u).post("/admin/founder/stepup/webauthn/register/verify", { credential, name }, { params: _fsp(t) }).then((r) => r.data);
export const stepupWebauthnAuthOptions = (t, u = null) =>
  _fs(t, u).post("/admin/founder/stepup/webauthn/auth/options", {}, { params: _fsp(t) }).then((r) => r.data);
export const stepupWebauthnAuthVerify = (t, credential, u = null) =>
  _fs(t, u).post("/admin/founder/stepup/webauthn/auth/verify", { credential }, { params: _fsp(t) }).then((r) => r.data);
export const stepupPasskeyRemove = (t, credId, u = null) =>
  _fs(t, u).delete(`/admin/founder/stepup/webauthn/${encodeURIComponent(credId)}`, { params: _fsp(t) }).then((r) => r.data);

export const networkSetContextSummary = (token, slug, text, userToken = null) =>
  adminClient(token, userToken).patch(
    `/admin/network/entities/${encodeURIComponent(slug)}/context-summary`,
    { context_summary: text },
    { params: token ? { "x-admin-token": token } : {} }).then((r) => r.data);
